import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, MessageSquareWarning } from "lucide-react";
import { Card, CardHeader, CardFooter } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { Button } from "@/components/ui/Button";
import { useShell } from "@/components/Shell";
import { relative, absolute } from "@/lib/format";

/**
 * Content gaps card. Top 3 unanswered question clusters, each with an
 * "Answer" CTA that routes into the inline editor in /gaps.
 */
export function ContentGapsCard() {
  const { home, loading } = useShell();

  if (loading && !home) return <ContentGapsSkeleton />;
  if (!home) return null;

  const { gaps } = home;

  return (
    <Card>
      <CardHeader
        title="Questions your bot couldn't answer"
        subtitle={gaps.total > 0 ? `${gaps.total} total this week` : "Top unanswered questions appear here"}
        right={
          gaps.total > 3 && (
            <Link
              to="/gaps"
              className="inline-flex items-center gap-1 text-xs text-coral hover:text-coral-hover"
            >
              See all {gaps.total} <ArrowRight size={12} strokeWidth={1.75} />
            </Link>
          )
        }
      />

      {gaps.items.length === 0 ? (
        <div className="px-5 pb-5 pt-2">
          <EmptyState
            icon={Sparkles}
            title="Your bot answered everything"
            description="When visitors ask something your bot can't ground in its data, we'll surface it here so you can write the answer."
          />
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {gaps.items.map((gap, i) => (
            <li key={i} className="flex items-start justify-between gap-4 px-5 py-3.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-text-strong">
                  &ldquo;{gap.question}&rdquo;
                </p>
                <p className="mt-1 text-xs text-text-soft">
                  <span className="tabular-nums">{gap.occurrences}</span>
                  {" "}
                  visitor{gap.occurrences === 1 ? "" : "s"} asked
                  {gap.last_asked_at && (
                    <>
                      {" · "}
                      <span title={absolute(gap.last_asked_at)} className="cursor-default">
                        last asked {relative(gap.last_asked_at)}
                      </span>
                    </>
                  )}
                </p>
              </div>
              <Link to={`/gaps?focus=${encodeURIComponent(gap.question)}`} className="shrink-0">
                <Button variant="primary" size="sm" iconRight={<ArrowRight size={12} strokeWidth={1.75} />}>
                  Answer
                </Button>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {gaps.total > 3 && (
        <CardFooter>
          <Link
            to="/gaps"
            className="inline-flex items-center gap-1 text-xs text-text-soft hover:text-text-strong"
          >
            <MessageSquareWarning size={12} strokeWidth={1.75} />
            Review all unanswered questions
          </Link>
        </CardFooter>
      )}
    </Card>
  );
}

function ContentGapsSkeleton() {
  return (
    <Card>
      <CardHeader title="Questions your bot couldn't answer" />
      <ul className="divide-y divide-line">
        {[0, 1, 2].map((i) => (
          <li key={i} className="flex items-start justify-between gap-4 px-5 py-4">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-8 w-20" />
          </li>
        ))}
      </ul>
    </Card>
  );
}
